<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex flex-wrap align-center gap-3">
            <div>
              <div class="text-subtitle-1">Progreso de alumnos</div>
              <div class="text-caption text-medium-emphasis">
                Avance de clases completadas y fecha de la última clase.
              </div>
            </div>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="busquedaAlumno"
              density="compact"
              hide-details
              clearable
              prepend-inner-icon="mdi-magnify"
              label="Buscar alumno"
              style="min-width: 240px"
            ></v-text-field>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pt-4 pb-1">
            <div class="dashboard-filters">
              <v-switch
                v-model="filtrarClasesSobrePagadas"
                inset
                hide-details
                color="primary"
                density="comfortable"
                label="Alumnos que deben"
                :true-value="true"
                :false-value="false"
              ></v-switch>
              <v-switch
                v-model="filtrarInactividadMaxima"
                inset
                hide-details
                color="primary"
                density="comfortable"
                label="Alumnos inactivos"
                :true-value="true"
                :false-value="false"
              ></v-switch>
              <v-switch
                v-model="includeFinalizados"
                inset
                hide-details
                color="primary"
                density="comfortable"
                label="Incluir finalizados"
                :true-value="true"
                :false-value="false"
              ></v-switch>
            </div>

          </v-card-text>
          <v-divider></v-divider>
          <v-card-text>
            <v-alert v-if="error" type="error" variant="tonal" class="mb-4">
              {{ error }}
            </v-alert>
            <div v-if="loading" class="d-flex justify-center py-10">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>
            <v-row v-if="alumnosFiltrados.length" class="gy-4" dense>
              <v-col
                v-for="alumno in alumnosFiltrados"
                :key="alumno.id"
                cols="12"
                md="6"
                lg="4"
              >
                <v-card class="alumno-progress-card" variant="outlined">
                  <div class="d-flex align-center gap-2 mb-3">
                    <div class="text-subtitle-2 font-weight-bold text-truncate">
                      {{ alumno.nombre }}
                    </div>
                    <v-spacer></v-spacer>
                    <v-chip size="small" color="primary" variant="tonal">
                      {{ alumno.matriculas.length }}
                      {{ alumno.matriculas.length === 1 ? 'matrícula' : 'matrículas' }}
                    </v-chip>
                  </div>

                  <div v-if="alumno.matriculas.length" class="matricula-stack">
                    <div
                      v-for="matricula in alumno.matriculas"
                      :key="matricula.id"
                      class="matricula-card"
                    >
                      <div
                        v-if="matricula.finalizacion"
                        class="d-flex align-center gap-2 mb-2"
                      >
                        <v-chip color="error" variant="tonal" size="small">
                          Finalizado
                          <template v-if="matricula.finalizacion.razon">
                            : {{ matricula.finalizacion.razon }}
                          </template>
                        </v-chip>
                        <div class="text-caption text-medium-emphasis">
                          {{ matricula.finalizacion.fecha || '' }}
                        </div>
                      </div>
                      <div class="d-flex align-center gap-3">
                        <v-progress-circular
                          :model-value="matricula.progreso"
                          :size="64"
                          :width="8"
                          color="primary"
                        >
                          <small class="font-weight-bold">{{ matricula.progreso }}%</small>
                        </v-progress-circular>
                        <div class="flex-grow-1 min-width-0">
                          <div class="d-flex align-center gap-2">
                            <div class="text-subtitle-2 font-weight-semibold text-truncate">
                              {{ matricula.curso || 'Curso sin nombre' }}
                            </div>
                            <v-spacer></v-spacer>
                            <v-tooltip
                              v-if="matricula.plazo_inactividad_alcanzado"
                              text="Tiempo máximo de inactividad alcanzado"
                            >
                              <template #activator="{ props }">
                                <v-icon
                                  v-bind="props"
                                  color="warning"
                                  size="18"
                                  class="blink-icon"
                                >
                                  mdi-clock-outline
                                </v-icon>
                              </template>
                            </v-tooltip>
                            <v-tooltip
                              v-if="matricula.clases_realizadas > (matricula.clase_debe_pagar || 0)"
                              text="Clases realizadas son mayores a las pagadas"
                            >
                              <template #activator="{ props }">
                                <v-icon
                                  v-bind="props"
                                  color="success"
                                  size="18"
                                  class="blink-icon"
                                >
                                  mdi-cash
                                </v-icon>
                              </template>
                            </v-tooltip>
                          </div>
                          <div class="meta-row text-caption text-medium-emphasis d-flex flex-wrap">
                            <span>Inscripción: {{ matricula.fecha_matricula || 'Sin fecha' }}</span>
                            <span>Última clase: {{ matricula.ultima_clase || 'Sin clases' }}</span>
                          </div>
                          <div class="progress-linear-spacing">
                            <v-progress-linear
                              :model-value="matricula.clases_realizadas"
                              :max="matricula.clases_totales || 0"
                              :min="0"
                              :buffer-value="matricula.clase_debe_pagar || 0"
                              buffer-opacity="1"        
                              buffer-color="amber-accent-4"
                              color="primary"
                              height="10"
                              rounded
                            ></v-progress-linear>
                            <div class="text-caption d-flex justify-space-between mt-1 text-medium-emphasis">
                              <span>Realizadas: {{ matricula.clases_realizadas }}</span>
                              <span>Total: {{ matricula.clases_totales || 0 }}</span>
                            </div>
                            <div class="text-caption text-medium-emphasis mt-1">
                              Pagado hasta clase: {{ matricula.clase_debe_pagar || '-' }}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="d-flex justify-end mt-3">
                        <v-btn
                          color="error"
                          variant="tonal"
                          size="small"
                          :disabled="!!matricula.finalizacion"
                          @click="abrirDialogoFinalizar(matricula)"
                        >
                          {{ matricula.finalizacion ? 'Finalizado' : 'Finalizar' }}
                        </v-btn>
                      </div>
                      <div class="totales-row text-caption text-medium-emphasis mt-3">
                        <div class="totales-item">
                          <span class="totales-label">Total</span>
                          <span class="totales-value">{{ matricula.total }}</span>
                        </div>
                        <div class="totales-item">
                          <span class="totales-label">Pagado</span>
                          <span class="totales-value">{{ matricula.pagado }}</span>
                        </div>
                        <div class="totales-item">
                          <span class="totales-label">Debe</span>
                          <span class="totales-value">{{ matricula.debe }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-caption text-disabled">
                    Sin matrículas registradas.
                  </div>
                </v-card>
              </v-col>
            </v-row>
            <div v-else-if="!loading" class="text-caption text-grey">
              No hay alumnos para mostrar.
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="dialogoFinalizar" max-width="520px">
      <v-card>
        <v-card-title class="d-flex align-center">
          <div class="text-subtitle-1">Finalizar matrícula</div>
          <v-spacer></v-spacer>
          <v-btn icon="mdi-close" variant="text" @click="cerrarDialogo" />
        </v-card-title>
        <v-card-text>
          <div class="text-body-2 mb-4" v-if="matriculaSeleccionada">
            {{ matriculaSeleccionada.curso || 'Curso sin nombre' }}
          </div>
          <v-alert
            v-if="errorFinalizacion"
            type="error"
            variant="tonal"
            density="comfortable"
            class="mb-4"
          >
            {{ errorFinalizacion }}
          </v-alert>
          <v-select
            v-model="formFinalizacion.razon_finalizacion_id"
            :items="razonesFinalizacion"
            :loading="cargandoRazones"
            item-title="descripcion"
            item-value="id"
            label="Motivo de finalización"
            density="comfortable"
            hide-details="auto"
            :return-object="false"
          ></v-select>
          <v-textarea
            v-model="formFinalizacion.comentario"
            class="mt-4"
            label="Comentario (opcional)"
            rows="3"
            auto-grow
            hide-details="auto"
          ></v-textarea>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="justify-end">
          <v-btn variant="text" @click="cerrarDialogo">Cancelar</v-btn>
          <v-btn
            color="primary"
            :loading="finalizando"
            :disabled="!formFinalizacion.razon_finalizacion_id"
            @click="finalizarMatricula"
          >
            Confirmar finalización
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { defineComponent, ref, computed, onMounted, watch, reactive } from 'vue'
import axios from 'axios'

export default defineComponent({
  setup() {
    const alumnosProgreso = ref([])
    const busquedaAlumno = ref('')
    const includeFinalizados = ref(false)
    const filtrarClasesSobrePagadas = ref(false)
    const filtrarInactividadMaxima = ref(false)
    const loading = ref(false)
    const error = ref('')

    const dialogoFinalizar = ref(false)
    const matriculaSeleccionada = ref(null)
    const razonesFinalizacion = ref([])
    const cargandoRazones = ref(false)
    const finalizando = ref(false)
    const errorFinalizacion = ref('')
    const formFinalizacion = reactive({
      razon_finalizacion_id: null,
      comentario: '',
    })

    const alumnosFiltrados = computed(() => {
      const term = busquedaAlumno.value.toLowerCase().trim()

      return alumnosProgreso.value
        .map((alumno) => {
          const matriculas = (alumno.matriculas || []).filter((matricula) => {
            if (
              filtrarClasesSobrePagadas.value &&
              !(matricula.clases_realizadas > (matricula.clase_debe_pagar || 0))
            ) {
              return false
            }

            if (filtrarInactividadMaxima.value && !matricula.plazo_inactividad_alcanzado) {
              return false
            }

            return true
          })

          return {
            ...alumno,
            matriculas,
          }
        })
        .filter((alumno) => {
          const coincideNombre = !term || alumno.nombre.toLowerCase().includes(term)
          return coincideNombre && alumno.matriculas.length > 0
        })
    })

    const cargarAlumnos = async () => {
      loading.value = true
      error.value = ''
      try {
        const response = await axios.get('/api/dashboard/alumnos', {
          params: {
            include_finalizados: !!includeFinalizados.value,
          },
        })
        alumnosProgreso.value =
          response.data.alumnos?.map((alumno) => ({
            ...alumno,
            matriculas: (alumno.matriculas || []).map((matricula) => {
              const progreso =
                matricula.clases_totales > 0
                  ? Math.round((matricula.clases_realizadas / matricula.clases_totales) * 100)
                  : 0
              return {
                ...matricula,
                progreso: Math.min(100, Math.max(0, progreso)),
              }
            }),
          })) || []
      } catch (err) {
        console.error('Error cargando alumnos', err)
        error.value = 'No se pudieron cargar los alumnos. Intenta nuevamente.'
      } finally {
        loading.value = false
      }
    }

    const cargarRazones = async () => {
      if (razonesFinalizacion.value.length) return
      cargandoRazones.value = true
      errorFinalizacion.value = ''
      try {
        const response = await axios.get('/api/razones-finalizacion')
        razonesFinalizacion.value = response.data || []
      } catch (err) {
        console.error('Error cargando razones de finalización', err)
        errorFinalizacion.value = 'No se pudieron cargar las razones de finalización.'
      } finally {
        cargandoRazones.value = false
      }
    }

    const abrirDialogoFinalizar = async (matricula) => {
      errorFinalizacion.value = ''
      matriculaSeleccionada.value = matricula
      formFinalizacion.razon_finalizacion_id = null
      formFinalizacion.comentario = ''
      await cargarRazones()
      dialogoFinalizar.value = true
    }

    const cerrarDialogo = () => {
      dialogoFinalizar.value = false
      errorFinalizacion.value = ''
      matriculaSeleccionada.value = null
    }

    const finalizarMatricula = async () => {
      if (!matriculaSeleccionada.value || !formFinalizacion.razon_finalizacion_id) return
      finalizando.value = true
      errorFinalizacion.value = ''
      try {
        await axios.post(`/api/matricula/${matriculaSeleccionada.value.id}/finalizar`, {
          razon_finalizacion_id: formFinalizacion.razon_finalizacion_id,
          comentario: formFinalizacion.comentario || null,
        })
        cerrarDialogo()
        await cargarAlumnos()
      } catch (err) {
        console.error('Error finalizando matrícula', err)
        errorFinalizacion.value =
          err?.response?.data?.message || 'No se pudo finalizar la matrícula.'
      } finally {
        finalizando.value = false
      }
    }

    watch(includeFinalizados, () => {
      cargarAlumnos()
    })

    onMounted(() => {
      cargarAlumnos()
    })

    return {
      alumnosFiltrados,
      busquedaAlumno,
      includeFinalizados,
      filtrarClasesSobrePagadas,
      filtrarInactividadMaxima,
      loading,
      error,
      dialogoFinalizar,
      matriculaSeleccionada,
      razonesFinalizacion,
      cargandoRazones,
      formFinalizacion,
      errorFinalizacion,
      finalizando,
      abrirDialogoFinalizar,
      cerrarDialogo,
      finalizarMatricula,
    }
  },
})
</script>

<style scoped>
.alumno-progress-card {
  padding: 16px;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.alumno-progress-card:hover {
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.matricula-stack {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.matricula-card {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  padding: 12px;
  background: rgba(0, 0, 0, 0.02);
}

.meta-row {
  column-gap: 12px;
  row-gap: 4px;
}

.progress-linear-spacing {
  margin-top: 12px;
}

.dashboard-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 20px;
  row-gap: 6px;
}

.totales-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.03);
}

.totales-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.totales-label {
  font-size: 11px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  opacity: 0.75;
}

.totales-value {
  font-size: 14px;
  font-weight: 600;
  color: inherit;
}

.blink-icon {
  animation: blink 1.2s ease-in-out infinite;
}

@keyframes blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}
</style>
